// Sync status for the shell's indicator ("Saving…", "Offline", "Live", first-load progress, "Updating
// visibility…").

import { useContext, useSyncExternalStore } from 'react';
import { SyncContext } from './context';

export interface SyncStatus {
  /** idle = up to date; pulling/pushing = working; offline = the last call failed on the network; error = other. */
  phase: 'starting' | 'idle' | 'pulling' | 'pushing' | 'offline' | 'error';
  /** Last successful pull. */
  lastSyncedAt: Date | null;
  /** Edits waiting to be pushed (outbox entries, including failed ones). */
  pending: number;
  /** Outbox entries the server refused ('invalid'); shown as "Not saved". */
  failed: number;
  /** A full resync is running (first load or visibility epoch change). */
  fullResync: boolean;
  /** Rows applied so far in the current pull (for a first-load progress bar). */
  progress: { applied: number } | null;
  error: string | null;
  /** A pull has completed at least once for this company (tells "no stores yet" from "still loading"). */
  initialFetchDone: boolean;
  /**
   * The server's change signal is connected (doorbell.ts) and its rings are arriving: teammates' saves show within
   * seconds. False again when a safety-net pull finds changes no ring announced.
   */
  live: boolean;
}

export const INITIAL_SYNC_STATUS: SyncStatus = {
  phase: 'starting',
  lastSyncedAt: null,
  pending: 0,
  failed: 0,
  fullResync: false,
  progress: null,
  error: null,
  initialFetchDone: false,
  live: false,
};

const noopSubscribe = () => () => {};
const initial = () => INITIAL_SYNC_STATUS;

/** The engine's live status; INITIAL_SYNC_STATUS outside SyncProvider. Re-renders only when the status changes. */
export function useSyncStatus(): SyncStatus {
  const ctx = useContext(SyncContext);
  const engine = ctx?.engine ?? null;
  return useSyncExternalStore(
    engine ? engine.subscribeStatus : noopSubscribe,
    engine ? engine.getStatus : initial,
    engine ? engine.getStatus : initial,
  );
}
